import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddVisitTypeCategoryModal from './AddVisitTypeCategoryModal';
import { hasPermission } from '../../../utils/permissions';
import {
  fetchVisitTypeCategories,
  fetchVisitTypeCategoryById,
  addVisitTypeCategory,
  updateVisitTypeCategory,
  selectVisitTypeCategories,
  selectVisitTypeCategoriesLoading,
  selectVisitTypeCategoriesError,
  selectVisitTypeCategoriesSuccessMessage,
  selectVisitTypeCategoriesTotal,
  selectVisitTypeCategoriesPage,
  selectVisitTypeCategoriesPageSize,
  selectVisitTypeCategoriesSortColumn,
  selectVisitTypeCategoriesSortDirection,
  selectVisitTypeCategoriesFilterName,
  selectSelectedVisitTypeCategory,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deleteVisitTypeCategory,
  bulkDeleteVisitTypeCategories,
  clearError,
  clearSuccessMessage,
} from './slice';

function VisitTypeCategories({ onSelectedIdsChange, onAddClick, onBulkDeleteClick, hideButtons = false, isActive = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const visitTypeCategories = useSelector(selectVisitTypeCategories);
  const loading = useSelector(selectVisitTypeCategoriesLoading);
  const error = useSelector(selectVisitTypeCategoriesError);
  const successMessage = useSelector(selectVisitTypeCategoriesSuccessMessage);
  const total = useSelector(selectVisitTypeCategoriesTotal);
  const page = useSelector(selectVisitTypeCategoriesPage);
  const pageSize = useSelector(selectVisitTypeCategoriesPageSize);
  const sortColumn = useSelector(selectVisitTypeCategoriesSortColumn);
  const sortDirection = useSelector(selectVisitTypeCategoriesSortDirection);
  const filterName = useSelector(selectVisitTypeCategoriesFilterName);
  const selectedVisitTypeCategory = useSelector(selectSelectedVisitTypeCategory);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [visitTypeCategoryToDelete, setVisitTypeCategoryToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions
  const canAddVisitTypeCategory = hasPermission('visittypecategory', 'addVisitTypeCategory');
  const canEditVisitTypeCategory = hasPermission('visittypecategory', 'editVisitTypeCategory');
  const canDeleteVisitTypeCategory = hasPermission('visittypecategory', 'deleteVisitTypeCategory');
  
  // Handler for selection change that also notifies parent
  const handleSelectionChange = useCallback((ids) => {
    setSelectedIds(ids);
    // Only notify parent if callback is provided and we're in embedded mode
    if (onSelectedIdsChange && hideButtons) {
      onSelectedIdsChange(ids);
    }
  }, [onSelectedIdsChange, hideButtons]);
  
  // Use local selectedIds
  const effectiveSelectedIds = selectedIds;

  // Fetch data whenever parent tab changes or when filters change
  useEffect(() => {
    dispatch(fetchVisitTypeCategories({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, isActive]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchVisitTypeCategories({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: t('visitTypeCategories.table.name'),
    },
    {
      key: 'description',
      label: t('visitTypeCategories.table.description'),
    },
    {
      key: 'updatedOn',
      label: t('visitTypeCategories.table.updatedOn'),
      render: (value) => (value ? new Date(value).toLocaleDateString('vi-VN') : '-'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    dispatch(fetchVisitTypeCategoryById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action
  const handleDelete = (row) => {
    setVisitTypeCategoryToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (visitTypeCategoryToDelete?.id) {
      dispatch(deleteVisitTypeCategory(visitTypeCategoryToDelete.id));
    }
    setShowDeleteDialog(false);
    setVisitTypeCategoryToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setVisitTypeCategoryToDelete(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (onBulkDeleteClick) {
      onBulkDeleteClick();
    } else {
      setShowBulkDeleteDialog(true);
    }
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteVisitTypeCategories(effectiveSelectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add visit type category
  const handleAddVisitTypeCategory = () => {
    if (onAddClick) {
      onAddClick();
    } else {
      setShowAddDialog(true);
    }
  };

  // Handle close add visit type category dialog
  const handleCloseAddVisitTypeCategory = () => {
    setShowAddDialog(false);
  };

  // Confirm add visit type category
  const handleConfirmAddVisitTypeCategory = (visitTypeCategoryData) => {
    dispatch(addVisitTypeCategory(visitTypeCategoryData));
    setShowAddDialog(false);
  };

  // Handle close edit visit type category dialog
  const handleCloseEditVisitTypeCategory = () => {
    setShowEditDialog(false);
  };

  // Confirm edit visit type category
  const handleConfirmEditVisitTypeCategory = (visitTypeCategoryData) => {
    dispatch(updateVisitTypeCategory(visitTypeCategoryData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', minHeight: 0 }}>
        {/* Action Buttons - Only show if not hidden */}
        {!hideButtons && (
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {canAddVisitTypeCategory && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddVisitTypeCategory}
                id="addVisitTypeCategory"
                name="addVisitTypeCategory"
                data-testid="visitTypeCategories-button-addVisitTypeCategory"
              >
                {t('visitTypeCategories.addVisitTypeCategory')}
              </Button>
            )}
            {canDeleteVisitTypeCategory && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={effectiveSelectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="visitTypeCategories-button-deleteSelected"
              >
                {t('visitTypeCategories.deleteSelected')}
              </Button>
            )}
          </Box>
        )}
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Input
                label={t('visitTypeCategories.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('visitTypeCategories.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
                data-testid="visitTypeCategories-input-filterName"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
          <Table
            id="visitTypeCategories-table"
            columns={columns}
            data={visitTypeCategories}
            selectable={true}
            selectedIds={effectiveSelectedIds}
            onSelectionChange={handleSelectionChange}
            onEdit={canEditVisitTypeCategory ? handleEdit : null}
            onDelete={canDeleteVisitTypeCategory ? handleDelete : null}
            loading={loading}
            emptyMessage={t('visitTypeCategories.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['name', 'description', 'updatedOn']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('visitTypeCategories.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('visitTypeCategories.deleteDialog.message', {
              name: visitTypeCategoryToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="visitTypeCategories-button-cancelDelete"
          >
            {t('visitTypeCategories.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="visitTypeCategories-button-confirmDelete"
          >
            {t('visitTypeCategories.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('visitTypeCategories.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('visitTypeCategories.bulkDeleteDialog.message', {
              count: effectiveSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="visitTypeCategories-button-cancelBulkDelete"
          >
            {t('visitTypeCategories.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="visitTypeCategories-button-confirmBulkDelete"
          >
            {t('visitTypeCategories.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Visit Type Category Modal */}
      <AddVisitTypeCategoryModal
        open={showAddDialog}
        onClose={handleCloseAddVisitTypeCategory}
        onConfirm={handleConfirmAddVisitTypeCategory}
      />

      {/* Edit Visit Type Category Modal */}
      <AddVisitTypeCategoryModal
        open={showEditDialog}
        onClose={handleCloseEditVisitTypeCategory}
        onConfirm={handleConfirmEditVisitTypeCategory}
        visitTypeCategoryData={selectedVisitTypeCategory}
        editMode={true}
      />
    </Box>
  );
}

export default VisitTypeCategories;

